import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Alert,
  Divider
} from '@mui/material';
import { Close } from '@mui/icons-material';

export default function SafetyLevelsDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
      How Subtidal Estimates Cyanobacteria Toxin Risk
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
          <Typography variant="body2">
            Subtidal does <strong>not</strong> measure or test for cyanobacteria toxins directly. Toxin presence and concentrations can only be determined through lab testing of water samples.
          </Typography>
        </Alert>

        <Typography variant="body1" paragraph>
            Instead, our system provides a risk estimate of possible toxin presence using three main inputs:
        </Typography>

        <Typography variant="h6" color="primary" gutterBottom>
          1. Core buoy measurements
        </Typography>
        <Box sx={{ pl: 2, mb: 3 }}>
          <Typography variant="body2" paragraph>
            • <strong>Cyanobacteria (phycocyanin)</strong> – primary indicator; phycocyanin is a cyanobacteria photosynthetic pigment
            • <strong>Chlorophyll a</strong> – general algae levels, Chlorophyll a is a algal photosynthetic pigment
            • <strong>Dissolved oxygen</strong> – oxygen variance and depletion can signal bloom stress or decay
            • <strong>Conductivity</strong> – changes in ionic content may signal nutrient or runoff inputs<br/>
            • <strong>Water temperature</strong> – influences bloom growth and stability
          </Typography>

          <Typography variant="body2" color="text.secondary">
            These parameters are measured at both the surface and near the bottom of the pond, which allows us to understand <strong>stratification and mixing dynamics</strong>. This is important because stratification can influence cyanobacteria presence, growth, or dissipation.
          </Typography>
        </Box>

        <Typography variant="h6" color="primary" gutterBottom>
          2. Supporting water and weather conditions
        </Typography>
        <Typography variant="body2" sx={{ pl: 2, mb: 3 }}>
        We also factor in conditions that shape bloom activity, including air temperature, wind, and precipitation. These do not directly indicate toxin levels but provide context for bloom risk and indicators of toxicity.
        </Typography>

        <Typography variant="h6" color="primary" gutterBottom>
          3. External comparisons
        </Typography>
        <Box sx={{ pl: 2, mb: 3 }}>
          <Typography variant="body2">
            • <strong>Lab sample results</strong> collected at the shoreline by partner organizations (e.g., APCC, local health departments).<br/>
            • <strong>Scum reports</strong> submitted by residents, which help flag localized bloom events.
          </Typography>
        </Box>

        <Typography variant="h6" color="primary" gutterBottom>
          Improving our estimates over time
        </Typography>
        <Typography variant="body2" paragraph sx={{ pl: 2 }}>
          We compare shoreline lab toxin results with our buoy's readings to identify the phycocyanin levels most often associated with toxins. We also plan to incorporate resident scum reports (with images and pond geolocation) to refine cyanobacteria risk estimates, making them more accurate for each specific pond.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Key takeaway:
        </Typography>
        <Typography variant="body2" component="div">
          • Cyanobacteria <strong>may or may not produce toxins</strong> at a given time.<br/>
          • Our dashboard provides <strong>risk estimates</strong>, not direct toxin measurements.<br/>
          • These estimates should be used for awareness, not as proof of water safety.
        </Typography>

        <Typography variant="body2" sx={{ mt: 3, fontStyle: 'italic', color: 'text.secondary' }}>
          The 0-3 risk index you see on the dashboard is calculated from these combined measurements, with 0 indicating undetectable risk and 3 indicating high risk estimates based on historical correlations with toxin presence.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}